import { AddressInputs } from "Components/AddressInputs";
import { useFieldProps } from "Components/Hooks";
import { Horizontal } from "Components/Horizontal";
import { Spacer } from "Components/Spacer";
import { Dropdown } from "Inputs/Dropdown";
import { RadioYesNoBool } from "Inputs/Radio";
import { EmailInput, Textbox } from "Inputs/Textbox";
import { ErrorMessage } from "Inputs/Tools";
import React from "react";
import { StandardComplexAddAdditionalInsuredModel } from "SharedConstants/Schema";
import { toObject } from "Tools";

enum FieldName {
  RequiresWaiverOfSubrogation = "RequiresWaiverOfSubrogation",
  RequiresNonContrib = "RequiresNonContrib",
  Relationship = "Relationship",
}

export function CalculateStandardAICost(
  _: StandardComplexAddAdditionalInsuredModel,
  values: any,
) {
  return (
    (values[FieldName.RequiresWaiverOfSubrogation] === "true" ? 100 : 0) +
    (values[FieldName.RequiresNonContrib] === "true" ? 100 : 0) +
    ((values[FieldName.Relationship] || "")
      .toLowerCase()
      .includes("independent contractor")
      ? 75
      : 0)
  );
}

export function AddStandardAIInput(
  props: StandardComplexAddAdditionalInsuredModel,
) {
  const relationshipField = useFieldProps(FieldName.Relationship);
  return (
    <>
      <RelationshipInput {...props} fieldName={relationshipField.name} />
      <Horizontal isRequired label="Name">
        <Textbox fieldName="Name" placeholder="Additional Insured Name" />
      </Horizontal>
      <Horizontal>
        <div className="is-italic">
          Note: This is the name that will appear on the Additional Insured's
          Certificate of Insurance.
        </div>
      </Horizontal>
      <AddressInputs isRequired label="Address" />
      <Horizontal label="Email">
        <EmailInput fieldName="Email" placeholder="name@company.com" />
      </Horizontal>
      <Horizontal label="Confirm Email">
        <EmailInput fieldName="VerifyEmail" placeholder="name@company.com" />
      </Horizontal>
      <Spacer />
      {props.ShouldShowWaiverOfSubrogation && (
        <Horizontal shouldNotPadLabel>
          <RadioYesNoBool fieldName="RequiresWaiverOfSubrogation">
            <div>
              <span className="has-text-weight-bold">
                <span style={{ color: "red" }}>* </span>
                Does this Additional Insured require a Waiver of Subrogation
                Endorsement?
              </span>{" "}
              (Cost: $100)
            </div>
          </RadioYesNoBool>
        </Horizontal>
      )}
      {(props.ShouldAlwaysShowNonContrib ||
        relationshipField.value.includes("Landlord")) && (
        <Horizontal shouldNotPadLabel>
          <RadioYesNoBool fieldName="RequiresNonContrib">
            <div>
              <span className="has-text-weight-bold">
                <span style={{ color: "red" }}>* </span>
                Does this Additional Insured require a Primary and
                Non-Contributory Endorsement?
              </span>{" "}
              (Cost: $100)
            </div>
          </RadioYesNoBool>
        </Horizontal>
      )}
      {props.ShouldShowCG2026Endorsement && (
        <Horizontal shouldNotPadLabel>
          <RadioYesNoBool fieldName="RequiresCG2026Endorsement">
            <div>
              <span className="has-text-weight-bold">
                <span style={{ color: "red" }}>* </span>
                Does this Additional Insured require a CG 20 26 Endorsement?
              </span>{" "}
              (No additional cost)
            </div>
          </RadioYesNoBool>
        </Horizontal>
      )}
    </>
  );
}

function RelationshipInput(
  props: StandardComplexAddAdditionalInsuredModel & {
    fieldName: string;
  },
) {
  const otherOption = "Other...";
  const field = useFieldProps(props.fieldName);

  const showOtherRelationshipInput =
    props.ShouldAllowSubmittingOtherRelationshipType &&
    field.value === otherOption;

  return (
    <>
      <Horizontal isRequired label="Relationship">
        <Dropdown
          fieldOptions={{
            name: props.fieldName,
            validate: (value: any): any => {
              if (
                !props.ShouldAllowSubmittingOtherRelationshipType &&
                value === otherOption
              ) {
                return `To bind an Additional Insured with an 'Other' relationship type, please contact us directly at ${props.ProducerEmail}`;
              }
            },
          }}
          options={toObject(props.RelationshipTypes.map(e => [e, e]))}
          value={
            props.IsIndependentContractor
              ? props.RelationshipTypes.find(e =>
                  e.toLowerCase().includes("independent"),
                )
              : undefined
          }
          placeholder="Select one..."
        />
      </Horizontal>
      {showOtherRelationshipInput && (
        <>
          <Horizontal>
            <div style={{ marginTop: "-0.8em" }}>
              <ErrorMessage>
                Please note if "Other" is selected, your quote will require
                further underwriting and cannot be automatically bound.
              </ErrorMessage>
            </div>
          </Horizontal>
          <Horizontal isRequired label="Other Relationship">
            <Textbox fieldName="Other Relationship" />
          </Horizontal>
        </>
      )}
    </>
  );
}
